import {
    Divider,
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerHeader,
    DrawerOverlay,
    useDisclosure,
    Link,
    Badge,
    Box,
    Center,
    Spinner,
    Table,
    Tbody,
    Td,
    Th,
    Thead,
    Tooltip,
    Tr,
    Text,
    useColorModeValue,
    Icon,
    HStack,
    useToast,
} from '@chakra-ui/react'
import React, {
    useEffect,
    useMemo,
    useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import { addLeadingSlash, removeTrailingSlash } from '../../../../utils/urlHelpers'
import { Job, JobSuccessfulResponse, JobType } from '../../../interfaces'
import moment from 'moment'
import { MdContentCopy } from 'react-icons/md'
import { useTable, useSortBy, Column } from 'react-table'
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa'


const typeColorScheme: Record<JobType, string> = {
    COPY: 'blue',
    MOVE: 'orange',
    RENAME: 'purple',
}


const PendingJobs = (): React.ReactElement => {
    const { t } = useTranslation()
    const {
        isOpen: isOpenDrawer,
        onOpen: onOpenDrawer,
        onClose: onCloseDrawer,
    } = useDisclosure()
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [jobs, setJobs] = useState<Job[]>([])
    const [isErrorMsg, setIsErrorMsg] = useState<string>('')

    const { data, get, loading, abort, error, response } = useFetch({
        cachePolicy: CachePolicies.NO_CACHE,
        data: [],
    })

    const border = useColorModeValue('gray.100', 'gray.700')

    const toast = useToast()

    const handleCopy = (id) => {
        navigator.clipboard.writeText(id)
            .then(() =>
                toast({
                    title: 'Copied to clipboard',
                    description: id,
                    status: 'success',
                    duration: 1500,
                    isClosable: true,
                })
            )
            .catch(() =>
                toast({
                    title: 'Copy failed',
                    status: 'error',
                    duration: 1500,
                    isClosable: true,
                })
            )
    }
    const columns = useMemo<Column<Job>[]>(() => [
        {
            Header: t('job_info', 'Job Info').toString(),
            id: 'jobInfo',
            accessor: values => values.jobType,
            Cell: ({ value, row }) => {
                return (<HStack>
                    <Tooltip hasArrow label={t("copy_job_id", "Copy job id")} >
                        <Box>
                            <Icon
                                as={MdContentCopy}
                                boxSize={6}
                                cursor="pointer"
                                onClick={() => handleCopy(row.original.id)}
                                title="Copy Job Id"
                            />
                        </Box>
                    </Tooltip>

                    <Badge marginLeft={"8px"} colorScheme={typeColorScheme[value]}>{value}</Badge>

                </HStack>)
            },
        },
        {
            Header: t('created_at', 'Created At').toString(),
            id: 'createdAt',
            accessor: values => new Date(values.createdAt ?? '').getTime(),
            Cell: ({ value }) => (
                <Text>{moment(value).format('MMM D, YYYY [at] hh:mm a')}</Text>
            ),
        },
        {
            Header: t('original_path', 'Original Path').toString(),
            accessor: values => values.oldPath,
            disableSortBy: true,
            Cell: ({ value }) => (
                <Tooltip label={value} hasArrow>
                    <Text noOfLines={2} wordBreak="break-all">{value}</Text>
                </Tooltip>
            ),
        },
        {
            Header: t('new_path', 'New Path').toString(),
            accessor: values => values.newPath,
            disableSortBy: true,
            Cell: ({ value }) => (
                <Tooltip label={value} hasArrow>
                    <Text noOfLines={2} wordBreak="break-all">{value}</Text>
                </Tooltip>
            ),
        },
    ], [])

    const dataMemo = useMemo(() => jobs, [jobs])

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
    } = useTable<Job>(
        { columns, data: dataMemo, initialState: { sortBy: [{ id: 'createdAt', desc: true }] } },
        useSortBy
    )
    useEffect(() => {
        if (isOpenDrawer) {
            void get(`api/v2/bulkJobs?status=PROCESSING,INITIATED&limit=100`)
        }

        return () => {
            abort();
        }
    }, [isOpenDrawer])

    useEffect(() => {
        setIsLoading(loading)
    }, [loading])

    useEffect(() => {
        if (error) {
            setIsErrorMsg(data?.message ?? t('something_went_wrong', 'Something went wrong. Please try again later.'))
        } else {
            setIsErrorMsg('')
        }
    }, [error, data?.message])

    useEffect(() => {
        if (response.ok && Array.isArray(data) && data.length > 0) {
            let jobs: Job[] = data.map(({ jobData, type, _id, status, createdAt }: JobSuccessfulResponse): Job => {
                let originalPath = ''
                let newPath = ''
                if (["COPY_FOLDER", "MOVE_FOLDER"].includes(type as string)) {
                    const folderName = removeTrailingSlash(jobData?.sourceFolderPath as string).split('/').pop();
                    originalPath = addLeadingSlash(jobData?.sourceFolderPath as string)
                    newPath = `${removeTrailingSlash(addLeadingSlash(jobData?.destinationPath as string))}/${folderName}`
                } else if (type === "RENAME_FOLDER") {
                    const newFolderName = jobData?.newFolderName as string;
                    originalPath = addLeadingSlash(jobData?.folderPath as string)
                    const pathArray = removeTrailingSlash(jobData?.folderPath as string).split('/');
                    pathArray.pop();
                    const parentFolderPath = pathArray.join('/');
                    newPath = `${removeTrailingSlash(addLeadingSlash(parentFolderPath))}/${newFolderName}`
                }
                return {
                    id: _id,
                    oldPath: originalPath,
                    newPath,
                    jobType: type.split('_')[0],
                    status,
                    createdAt,
                }
            })
            setJobs(jobs)
        } else {
            setJobs([])
        }
    }, [data, response.ok])

    return (
        <>
            {!isOpenDrawer && (<Link
                color="appDarkIndigo"
                fontSize="12px"
                padding="0 10px"
                _hover={{
                    textDecoration: 'none',
                    backgroundColor: 'appPaleGreyTwo',
                }}
                onClick={onOpenDrawer}
            >
                {t('pending_jobs', 'Pending Jobs')}{' '}
            </Link>)}
            <Drawer
                isOpen={isOpenDrawer}
                size="lg"
                onClose={onCloseDrawer}
            >
                <DrawerOverlay />
                <DrawerContent>
                    <DrawerCloseButton />
                    <DrawerHeader>
                        {t('pending_jobs', 'Pending Jobs')}
                    </DrawerHeader>
                    <Divider />
                    <DrawerBody>
                        {isLoading ?
                            (
                                <Center height="100%">
                                    <Spinner
                                        thickness="4px"
                                        speed="0.65s"
                                        emptyColor="appPaleGrey"
                                        color="appBlue"
                                        size="xl"
                                    />
                                </Center>) :
                            isErrorMsg ?
                                (<Center padding="2em 0em">
                                    <Text>{isErrorMsg}</Text>
                                </Center>
                                ) :
                                (<>
                                    <Box borderWidth="1px" borderColor={border} display="block" borderRadius="lg" width="100%">
                                        <Table {...getTableProps} size="sm" variant="simple" overflowX="hidden" tableLayout="fixed" width={'100%'}>
                                            <colgroup>
                                                <col style={{ width: '18%' }} />
                                                <col style={{ width: '22%' }} />
                                                <col style={{ width: '30%' }} />
                                                <col style={{ width: '30%' }} />
                                            </colgroup>

                                            <Thead >
                                                {headerGroups.map(headerGroup => {
                                                    const { key, ...restHeaderGroupProps } =
                                                        headerGroup.getHeaderGroupProps()
                                                    return (
                                                        <Tr key={key} {...restHeaderGroupProps}>
                                                            {headerGroup.headers.map(column => {
                                                                const { key: keyCol, ...restColumn } =
                                                                    column.getHeaderProps(column.getSortByToggleProps())
                                                                return (
                                                                    <Th key={keyCol} textTransform="none" {...restColumn}>
                                                                        {column.render('Header')}
                                                                        {!column.disableSortBy ? (
                                                                            <Box
                                                                                as="span"
                                                                                pl="2"
                                                                                color='appBattleshipGrey'
                                                                            >
                                                                                {column.isSorted ? (
                                                                                    column.isSortedDesc ? (
                                                                                        <Icon
                                                                                            as={FaSortUp}
                                                                                            aria-label={t(
                                                                                                'sorted_descending',
                                                                                                'sorted descending',
                                                                                            )}
                                                                                        />
                                                                                    ) : (
                                                                                        <Icon
                                                                                            as={FaSortDown}
                                                                                            aria-label={t(
                                                                                                'sorted_ascending',
                                                                                                'sorted ascending',
                                                                                            )}
                                                                                        />
                                                                                    )
                                                                                ) : (
                                                                                    <Icon
                                                                                        as={FaSort}
                                                                                        aria-label={t(
                                                                                            'sort_by_column',
                                                                                            'sort by column',
                                                                                        )}
                                                                                    />
                                                                                )}
                                                                            </Box>
                                                                        ) : null}
                                                                    </Th>
                                                                )
                                                            })}
                                                        </Tr>
                                                    )
                                                })}
                                            </Thead>

                                            <Tbody  {...getTableBodyProps()}>
                                                {rows.map(row => {
                                                    prepareRow(row)
                                                    const { key, ...restRowProps } = row.getRowProps()
                                                    return (
                                                        <Tr key={key} {...restRowProps}>
                                                            {row.cells.map(cell => {
                                                                const { key: keyRow, ...restCellProps } =
                                                                    cell.getCellProps()
                                                                return (
                                                                    <Td key={keyRow} {...restCellProps}>
                                                                        {cell.render('Cell')}
                                                                    </Td>
                                                                )
                                                            })}
                                                        </Tr>
                                                    )
                                                })}
                                            </Tbody>
                                        </Table>
                                    </Box>
                                    {(rows.length === 0) && (
                                        <Center padding="2em 0em">
                                            <Text>{t('no_pending_jobs', 'No pending jobs')}</Text>
                                        </Center>
                                    )}
                                </>)
                        }
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </>
    )
}

export default PendingJobs
